import { Flex, InputNumber, RadioChangeEvent } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getDebtListByPointIdThunk,
    getPointInfoCollectionThunk,
} from "tm-shared/api";
import { PaymentForm, Point, TaskTakeDebt } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";
import { v7 } from "uuid";

import { ROUTES } from "@/constants";
import { SVScanButton } from "@/features/SupervisorScan";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointDebtsSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { WarningModal } from "@/shared/modals";
import { Meta } from "@/shared/seo";
import { BaseModal } from "@/shared/ui";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { UniversalTabs } from "@/shared/ui/UniversalTabs";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { isManagerRole } from "@/utils/business/isManagerRole";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ByMarket } from "./ByMarket";
import { DebtSummaryHeader } from "./DebtSummaryHeader";
import DiscardPaymentModal from "./DiscardPaymentModal";
import { ButtonsWrapper, StyledMainContent } from "./Styled";

interface TakeDebtPageProps {
    point: Point;
    task: TaskTakeDebt;
}

export const TakeDebtPage: FC<TakeDebtPageProps> = ({ point, task }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { makeTaskCompleteCommands } = useMakeCommand();
    const taskId = task.id;
    const pointId = point.id;

    const offlineMode = useOfflineModeSelector();
    const role = useProtectedUserRoleSelector();
    const employee = useAppSelector((state) => state.user.employee);
    const debtPointList = usePointDebtsSelector(pointId);
    const team = useAppSelector((s) => s.manager.team);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);

    const warningModalKey = useMemo(() => v7(), []);
    const [modal, setModal] = useState<
        null | "warning" | "discharged" | "confirm"
    >(null);
    const [currentTab, setCurrentTab] = useState<string>("market");
    const [discardPaymentType, setDiscardPaymentType] = useState<number>(1);
    const [currentPointInvoiceIds, setCurrentPointInvoiceIds] = useState<
        string[]
    >([]);

    const [debtValue, setDebtValue] = useState<number>(0);

    const normalizedDebtPointList = useMemo(
        () => debtPointList ?? {},
        [debtPointList],
    );

    const totalDebtSum = useMemo(
        () =>
            Object.values(normalizedDebtPointList).reduce(
                (acc, item) => acc + (item.debt || 0),
                0,
            ),
        [normalizedDebtPointList],
    );

    const chosenDebtPointSum = useMemo(
        () =>
            currentPointInvoiceIds.reduce(
                (acc, item) => acc + (debtPointList?.[item]?.debt || 0),
                0,
            ),
        [currentPointInvoiceIds, debtPointList],
    );

    const maxAllowedSum = useMemo(
        () =>
            Object.values(normalizedDebtPointList).reduce(
                (acc, { paymentForm, debt }) =>
                    paymentForm === PaymentForm.Cash2 ? acc + debt : acc,
                0,
            ),
        [normalizedDebtPointList],
    );

    const currentMaxAllowedSum = useMemo(
        () =>
            currentPointInvoiceIds.length ? chosenDebtPointSum : maxAllowedSum,
        [currentPointInvoiceIds, chosenDebtPointSum, maxAllowedSum],
    );

    const taskCompleteLocal = useCallback(() => {
        dispatch(setTaskCompleted({ targetId: pointId, taskId }));

        navigate(ROUTES.WorkPlan, { replace: true });
    }, [dispatch, pointId, navigate, taskId]);

    const handleOpenConfirmModal = useCallback(() => {
        setModal("confirm");
    }, []);

    const [handleSendPayment, sendPaymentLoading] = useAsyncAction(async () => {
        const normalizedDebtValue = +debtValue.toFixed(2);

        if (normalizedDebtValue > +currentMaxAllowedSum.toFixed(2)) {
            setModal("warning");
            return;
        }

        const consignmentsIds = currentPointInvoiceIds;

        const payerId = isManagerRole(role)
            ? team?.members?.find((m) => m.userId === activeSchedule?.userId)
                  ?.id
            : employee?.id;

        if (!payerId) throw new Error("payerId is required");

        const paymentParams = {
            pointId,
            consignmentsIds: consignmentsIds.length
                ? consignmentsIds
                : undefined,
            amount: normalizedDebtValue,
            isFopPay: false,
            payerId,
        };

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: true, ...paymentParams },
            },
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: {
                    pointId,
                    taskId,
                },
                options: {
                    todayOnly: true,
                },
                commands: [
                    makeCommand({
                        type: "postInvoiceMakePaymentByPoint",
                        title: `Створення рахунку на суму ${formatUAHStandard(
                            paymentParams.amount,
                        )}`,
                        params: paymentParams,
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "getDebtListByPointIdThunk",
                        title: "Оновлення боргів торгової точки",
                        params: { pointId },
                    }),
                    makeCommand({
                        type: "getPointInfoCollectionWorkThunk",
                        title: "Оновлення інформації про торгову точку",
                        params: { ids: [pointId] },
                    }),
                ],
            }),
        );

        taskCompleteLocal();
    });

    const [handleDiscardPayment, discardPaymentLoading] = useAsyncAction(
        async () => {
            const comment =
                discardPaymentType === 1
                    ? "Відсутні кошти у ТТ на даний момент."
                    : "Зроблено безготівкове перерахування.";

            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: { result: false, comment },
                },
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        pointId,
                        taskId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: taskCompleteCommands,
                }),
            );

            taskCompleteLocal();
        },
    );

    useEffect(() => {
        if (!offlineMode) {
            dispatch(getPointInfoCollectionThunk({ ids: [pointId] }));
            dispatch(getDebtListByPointIdThunk({ pointId }));
        }
    }, [dispatch, pointId, offlineMode]);

    const isSending = sendPaymentLoading || discardPaymentLoading;

    return (
        <>
            <Meta title="Забрати борг" />
            <Header
                title="Борг"
                back
                rightActions={
                    <SVScanButton pointId={pointId} taskId={taskId} />
                }
            />

            <MainContainer>
                <StyledMainContent>
                    <Flex vertical gap={12}>
                        <Flex align="center" justify="center">
                            <UniversalTabs
                                value={currentTab}
                                onChange={setCurrentTab}
                                tabs={[
                                    { value: "market", label: "По магазину" },
                                ]}
                                maxWidth="280px"
                            />
                        </Flex>

                        <DebtSummaryHeader
                            totalAmount={totalDebtSum}
                            invoicesAmount={chosenDebtPointSum}
                        />

                        <PrimaryText fontSize={16} fontWeight="500">
                            {point?.name}
                        </PrimaryText>

                        <Flex vertical gap={8}>
                            <SecondaryText color="disabled">
                                Введіть суму грошей, яку бажаєте забрати
                            </SecondaryText>

                            <InputNumber
                                placeholder="0000.0"
                                size="large"
                                onFocus={handleFocus}
                                value={debtValue}
                                parser={(value) =>
                                    +(value || "")
                                        .replace(",", ".")
                                        .replace(/[^0-9.]/g, "")
                                }
                                inputMode="decimal"
                                onChange={(value) =>
                                    setDebtValue(Number(value) || 0)
                                }
                            />
                        </Flex>
                    </Flex>

                    {currentTab === "market" && (
                        <ByMarket
                            selectedInvoiceIds={currentPointInvoiceIds}
                            setSelectedInvoiceIds={setCurrentPointInvoiceIds}
                            isLoading={isSending}
                            invoicesById={normalizedDebtPointList}
                        />
                    )}
                </StyledMainContent>

                <ButtonsWrapper>
                    {debtValue > 0 ? (
                        <BaseButton
                            onClick={handleOpenConfirmModal}
                            loading={sendPaymentLoading}
                            type="primary"
                        >
                            Вивести кошти
                        </BaseButton>
                    ) : (
                        <BaseButton
                            type="primary"
                            onClick={() => setModal("discharged")}
                            loading={isSending}
                        >
                            Немає можливості оплати
                        </BaseButton>
                    )}
                </ButtonsWrapper>
            </MainContainer>

            <WarningModal
                modalKey={warningModalKey}
                width="92%"
                isModalOpened={modal === "warning"}
                cancel={() => setModal(null)}
                title="Увага!"
            >
                <Flex vertical gap={20}>
                    <PrimaryText>
                        Бажана сума, яку ви хочете забрати:{" "}
                        <strong>{formatUAHStandard(debtValue)}</strong>
                    </PrimaryText>

                    <PrimaryText>
                        Максимально допустима сума:{" "}
                        <strong>
                            {formatUAHStandard(currentMaxAllowedSum)}
                        </strong>
                    </PrimaryText>
                </Flex>
            </WarningModal>

            <BaseModal
                open={modal === "confirm"}
                onClose={() => !sendPaymentLoading && setModal(null)}
                onConfirm={handleSendPayment}
                confirmLoading={sendPaymentLoading}
                title="Підтвердження"
                confirmText="Підтвердити"
                cancelText="Повернутися назад"
                closable={!sendPaymentLoading}
                keyboard={!sendPaymentLoading}
                mask={{ closable: !sendPaymentLoading }}
            >
                <PrimaryText fontSize={16}>
                    Ви дійсно хочете вивести{" "}
                    <strong>{formatUAHStandard(debtValue)}</strong>?
                </PrimaryText>
            </BaseModal>

            <DiscardPaymentModal
                isModalOpened={modal === "discharged"}
                handleCloseModal={() => setModal(null)}
                discardPaymentType={discardPaymentType}
                onDiscardPaymentTypeChange={(e: RadioChangeEvent) =>
                    setDiscardPaymentType(e.target.value)
                }
                handleDiscardPayment={handleDiscardPayment}
                loading={discardPaymentLoading}
            />
        </>
    );
};
